"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    Avancer,
    Curseur,
    Ecran,
    Liste,
    Montant,
    Question,
    Recommencer,
    Resultat,
    Segments,
    euros,
    remplir,
    useParcours,
} from "./Journey";
import { SEUIL_DIVIDENDES_EURL } from "@/lib/tools/salaryDividends";
import { comparerOptionIs } from "@/lib/tools/corporateTaxOption";
import { expliquerOptionIs } from "@/lib/tools/explanations";
import {
    IndiceExplication,
    MontantExplicable,
    PanneauExplication,
    phraseAvecMontants,
    useExplications,
} from "./Explanation";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * Passer à l'impôt sur les sociétés : ce que l'option change pour moi.
 *
 * L'IS NE FAIT PAS PAYER MOINS, IL FAIT PAYER PLUS TARD. C'est la phrase que
 * l'écran veut laisser, parce que c'est celle qu'on ne lit nulle part. Deux
 * réponses, séparées :
 *
 *   - si vous vous versez tout chaque année, l'IS change peu, et l'écran dit
 *     de combien ;
 *   - si vous ne prenez que ce dont vous avez besoin, l'IS laisse le reste
 *     dans la société après un impôt de 15 % : l'écran le chiffre, et rappelle
 *     que cet argent paiera encore en sortant.
 *
 * LES RÈGLES DE L'OPTION SONT DITES À LA FIN, parce qu'elles décident souvent
 * à la place des chiffres : un délai de trois mois, et un retour possible
 * pendant cinq exercices seulement.
 *
 * TOUT RESTE DANS LE NAVIGATEUR : aucun appel réseau, aucun stockage.
 */

const PHASES = {
    forme: 0,
    micro: 0,
    activite: 0,
    cipav: 0,
    benefice: 1,
    capital: 1,
    besoin: 1,
    foyer: 2,
    autresRevenus: 2,
    resultat: 3,
};

const L = {
    fr: {
        phases: ["Entreprise", "Chiffres", "Foyer"],
        retour: "Retour",
        forme: {
            titre: "Où en êtes-vous aujourd'hui ?",
            aide: "Selon votre forme d'entreprise, vous n'accédez pas à l'impôt sur les sociétés de la même façon.",
            choix: [
                {
                    cle: "ei",
                    icone: "user-id-linear",
                    titre: "Entreprise individuelle au réel",
                    detail: "Vous déclarez un bénéfice, pas un chiffre d'affaires abattu.",
                },
                {
                    cle: "eurl",
                    icone: "buildings-2-linear",
                    titre: "EURL à l'impôt sur le revenu",
                    detail: "Vous êtes le seul associé, et vous n'avez jamais choisi l'impôt sur les sociétés.",
                },
                {
                    cle: "micro",
                    icone: "case-minimalistic-linear",
                    titre: "Micro-entreprise",
                    detail: "Vous déclarez votre chiffre d'affaires à l'Urssaf.",
                },
            ],
        },
        micro: {
            titre: "En micro-entreprise, vous ne pouvez pas encore opter",
            aide: "Seul un entrepreneur individuel au régime réel peut choisir l'impôt sur les sociétés. Il vous faudrait d'abord quitter la micro.",
            points: [
                "Avant de penser à l'IS, regardez si quitter la micro vous rapporterait déjà quelque chose. C'est la part de vos dépenses qui en décide.",
            ],
            lien: "Quand quitter la micro-entreprise",
        },
        activite: {
            titre: "Quelle est votre activité ?",
            aide: "Elle change une partie de vos cotisations d'indépendant.",
            choix: [
                {
                    cle: "services",
                    icone: "shop-linear",
                    titre: "Commerce ou prestations",
                    detail: "Vous vendez, ou vous rendez des services à des entreprises ou à des particuliers.",
                },
                {
                    cle: "artisan",
                    icone: "settings-minimalistic-linear",
                    titre: "Artisanat",
                    detail: "Vous êtes immatriculé comme artisan.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square-linear",
                    titre: "Activité libérale",
                    detail: "Conseil, développement, design, formation, rédaction.",
                },
                {
                    cle: "cipav",
                    icone: "stethoscope-linear",
                    titre: "Profession réglementée",
                    detail: "Architecte, psychologue, ostéopathe : vous cotisez à la Cipav.",
                },
            ],
        },
        cipav: {
            titre: "Pour la Cipav, nous n'avons pas encore le calcul",
            aide: "À la Cipav, vous cotisez pour votre retraite selon un barème à part, que nous ne savons pas encore calculer au centime. Plutôt qu'un chiffre faux, nous préférons ne rien vous afficher.",
            points: ["Les règles de l'option, elles, valent aussi pour vous : trois mois pour la prendre, et cinq exercices pour revenir en arrière."],
        },
        benefice: {
            titre: "Quel est votre bénéfice de l'année ?",
            aide: "C'est ce que gagne votre activité avant vos cotisations et votre impôt : votre chiffre d'affaires moins vos dépenses.",
            libelle: "Bénéfice avant cotisations",
            suivant: "Continuer",
        },
        capital: {
            titre: "Combien avez-vous mis dans votre EURL ?",
            aide: "Comptez le capital, les primes d'émission, et l'argent laissé sur votre compte courant. À l'IS, vos dividendes paieront des cotisations au-delà de {seuil} de ce montant.",
            libelle: "Capital, primes et compte courant",
            suivant: "Continuer",
        },
        besoin: {
            titre: "De combien avez-vous besoin pour vivre dans l'année ?",
            aide: "Après cotisations et impôt. À l'IS, c'est ce que vous vous verseriez, et le reste resterait dans votre entreprise.",
            libelle: "Ce que vous voulez toucher dans l'année",
            suivant: "Continuer",
        },
        foyer: {
            titre: "Qui compose votre foyer fiscal ?",
            aide: "Votre impôt sur le revenu dépend de tout votre foyer, et il pèse lourd dans la comparaison.",
            situation: "Situation",
            seul: "Seul",
            couple: "En couple",
            enfants: "Enfants à charge",
            suivant: "Continuer",
        },
        autresRevenus: {
            titre: "Y a-t-il d'autres revenus dans votre foyer ?",
            aide: "Un salaire, une pension, les revenus de votre conjoint. Nous ne comptons que l'impôt en plus que votre activité vous fait payer.",
            libelle: "Autres revenus imposables du foyer",
            aucun: "Aucun autre revenu",
            suivant: "Voir ce que l'IS changerait",
        },
        resultat: {
            titre: "Passer à l'IS",
            toutTitre: {
                is: "Si vous vous versez tout, l'IS vous ferait gagner {gain} par an",
                ir: "Si vous vous versez tout, l'IS vous coûterait {gain} par an",
                egal: "Si vous vous versez tout, l'IS ne change presque rien",
            },
            toutDetail: "À l'impôt sur le revenu, il vous reste {ir}. À l'IS, avec le meilleur partage, {is}.",
            pourquoi:
                "L'écart vient surtout de l'abattement de 10 % pour frais professionnels. Votre rémunération de gérant y a droit, pas votre bénéfice d'indépendant.",
            garde: {
                titre: "Si vous ne prenez que {besoin} par an",
                texte:
                    "À l'impôt sur le revenu, tout votre bénéfice est imposé dans l'année : ce qui dépasse votre besoin, {epargne}, est à vous et déjà imposé. À l'IS, votre entreprise garde {reserve}, après {impot} d'impôt sur les sociétés.",
                texteCourt:
                    "À l'impôt sur le revenu, votre bénéfice ne vous laisse que {ir} après impôt, moins que votre besoin. À l'IS, en vous versant {besoin}, votre entreprise garderait {reserve}, après {impot} d'impôt sur les sociétés.",
                piege:
                    "Attention, cet argent n'est pas encore à vous. Le jour où vous le sortirez en dividendes, vous paierez l'impôt sur le revenu et, au-delà de {seuil}, des cotisations. En clair, vous payez plus tard, mais vous payez. D'ici là, cet argent peut servir à votre entreprise.",
                seuilEi: "10 % du bénéfice",
                carteIr: "À l'impôt sur le revenu : votre épargne, déjà imposée",
                carteIs: "À l'IS : ce que garde votre entreprise",
                impossible:
                    "Votre besoin dépasse ce que votre bénéfice peut vous verser en rémunération à l'IS : votre entreprise ne garderait rien.",
            },
            regles: {
                titre: "Les règles à connaître avant d'opter",
                items: {
                    delai: "Vous devez prévenir votre service des impôts avant la fin du troisième mois de l'exercice que l'option doit couvrir.",
                    retour: "Vous pouvez renoncer à l'impôt sur les sociétés jusqu'au mois qui précède le premier acompte d'IS du cinquième exercice suivant. Après, l'option devient définitive.",
                    ei: "Pour une entreprise individuelle, l'option passe par l'assimilation à une EURL, et celle-là est irrévocable.",
                },
            },
            lienPartage: "Rémunération ou dividendes : trouver le bon partage",
            hypotheses: {
                titre: "Pour ce calcul, nous avons supposé que :",
                items: [
                    "à l'IS, vous êtes gérant et vous cotisez comme un indépendant, sur votre rémunération comme aujourd'hui sur votre bénéfice ;",
                    "quand vous vous versez tout, vous choisissez le partage entre rémunération et dividendes le plus favorable à votre foyer ;",
                    "les honoraires de l'expert-comptable ne sont pas comptés ;",
                    "l'impôt compté est celui que votre activité ajoute à votre foyer, pas tout l'impôt du foyer.",
                ],
            },
            avertissement:
                "Ces chiffres sont indicatifs. L'option engage votre entreprise pour cinq exercices au moins : décidez-la avec un expert-comptable.",
            recommencer: "Refaire le calcul",
        },
    },
    en: {
        phases: ["Business", "Figures", "Household"],
        retour: "Back",
        forme: {
            titre: "Where do you stand today?",
            aide: "Depending on your form of business, you do not reach corporate tax the same way.",
            choix: [
                {
                    cle: "ei",
                    icone: "user-id-linear",
                    titre: "Sole trader on actual expenses",
                    detail: "You declare a profit, not a turnover after allowance.",
                },
                {
                    cle: "eurl",
                    icone: "buildings-2-linear",
                    titre: "EURL under income tax",
                    detail: "You are the only shareholder, and you have never chosen corporate tax.",
                },
                {
                    cle: "micro",
                    icone: "case-minimalistic-linear",
                    titre: "Micro-entreprise",
                    detail: "You declare your turnover to the Urssaf.",
                },
            ],
        },
        micro: {
            titre: "Under the micro-entreprise, you cannot opt yet",
            aide: "Only a sole trader under an actual-expenses regime can choose corporate tax. You would first have to leave the micro.",
            points: [
                "Before thinking about corporate tax, see whether leaving the micro would already gain you something. The share of your spending decides it.",
            ],
            lien: "When to leave the micro-entreprise",
        },
        activite: {
            titre: "What is your activity?",
            aide: "It changes part of your self-employed contributions.",
            choix: [
                {
                    cle: "services",
                    icone: "shop-linear",
                    titre: "Trade or services",
                    detail: "You sell goods, or provide services to businesses or individuals.",
                },
                {
                    cle: "artisan",
                    icone: "settings-minimalistic-linear",
                    titre: "Craft trade",
                    detail: "You are registered as a craftsperson.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square-linear",
                    titre: "Liberal profession",
                    detail: "Consulting, development, design, training, writing.",
                },
                {
                    cle: "cipav",
                    icone: "stethoscope-linear",
                    titre: "Regulated profession",
                    detail: "Architect, psychologist, osteopath: you contribute to the Cipav.",
                },
            ],
        },
        cipav: {
            titre: "We do not have the Cipav calculation yet",
            aide: "With the Cipav, your pension contributions follow their own scale, which we cannot yet compute to the cent. Rather than show you a wrong figure, we prefer to show nothing.",
            points: ["The option's rules do apply to you: three months to take it, and five financial years to go back."],
        },
        benefice: {
            titre: "What is your profit for the year?",
            aide: "That is what your business makes before your contributions and income tax: your turnover minus your expenses.",
            libelle: "Profit before contributions",
            suivant: "Continue",
        },
        capital: {
            titre: "How much have you put into your EURL?",
            aide: "Count the share capital, share premiums, and money left in your current account. Under corporate tax, your dividends will carry contributions beyond {seuil} of that amount.",
            libelle: "Capital, premiums and current account",
            suivant: "Continue",
        },
        besoin: {
            titre: "How much do you need to live on in the year?",
            aide: "After contributions and income tax. Under corporate tax, that is what you would pay yourself, and the rest would stay in your business.",
            libelle: "What you want to receive in the year",
            suivant: "Continue",
        },
        foyer: {
            titre: "Who is in your tax household?",
            aide: "Your income tax depends on your whole household, and it weighs heavily in the comparison.",
            situation: "Situation",
            seul: "Single",
            couple: "Couple",
            enfants: "Dependent children",
            suivant: "Continue",
        },
        autresRevenus: {
            titre: "Is there other income in your household?",
            aide: "A salary, a pension, your partner's income. We only count the extra tax your business makes you pay.",
            libelle: "Other taxable household income",
            aucun: "No other income",
            suivant: "See what corporate tax would change",
        },
        resultat: {
            titre: "Switching to corporate tax",
            toutTitre: {
                is: "If you take everything, corporate tax would gain you {gain} a year",
                ir: "If you take everything, corporate tax would cost you {gain} a year",
                egal: "If you take everything, corporate tax changes almost nothing",
            },
            toutDetail: "Under income tax, you keep {ir}. Under corporate tax, with the best split, {is}.",
            pourquoi:
                "The gap mostly comes from the 10% allowance for professional expenses. Your pay as a manager is entitled to it, your profit as a sole trader is not.",
            garde: {
                titre: "If you only take {besoin} a year",
                texte:
                    "Under income tax, all your profit is taxed in the year: what exceeds your need, {epargne}, is yours and already taxed. Under corporate tax, your business keeps {reserve}, after {impot} of corporate tax.",
                texteCourt:
                    "Under income tax, your profit only leaves you {ir} after tax, less than your need. Under corporate tax, paying yourself {besoin}, your business would keep {reserve}, after {impot} of corporate tax.",
                piege:
                    "Careful: that money is not yours yet. The day you take it out as dividends, you will pay income tax and, beyond {seuil}, contributions. Put simply, you pay later, but you still pay. Until then, your business can put the money to use.",
                seuilEi: "10% of profit",
                carteIr: "Under income tax: your savings, already taxed",
                carteIs: "Under corporate tax: what your business keeps",
                impossible:
                    "Your need exceeds what your profit can pay you as salary under corporate tax: your business would keep nothing.",
            },
            regles: {
                titre: "The rules to know before opting",
                items: {
                    delai: "You must notify your tax office before the end of the third month of the financial year the option is to cover.",
                    retour: "You can renounce corporate tax up to the month before the first corporate tax instalment of the fifth following financial year. After that, the option becomes final.",
                    ei: "For a sole trader, the option goes through assimilation to an EURL, and that step is irrevocable.",
                },
            },
            lienPartage: "Salary or dividends: find the right split",
            hypotheses: {
                titre: "For this calculation, we assumed that:",
                items: [
                    "under corporate tax, you are a manager and pay contributions as a self-employed person, on your pay just as today on your profit;",
                    "when you take everything, you choose the split between pay and dividends that suits your household best;",
                    "the accountant's fees are not counted;",
                    "the tax counted is what your business adds to your household, not the household's whole tax.",
                ],
            },
            avertissement:
                "These figures are indicative. The option commits your business for five financial years at least: decide on it with an accountant.",
            recommencer: "Start again",
        },
    },
};

export default function PasserIs({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const r = t.resultat;

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    // La micro et la CIPAV ne sont jamais des valeurs de l'outil : ce sont des
    // écrans de refus, où la reprise mène directement. Ici « eurl » est l'EURL
    // à l'impôt sur le revenu (le traducteur s'en charge).
    const { valeurs: dossier } = useDossierOutil();
    const formeDossier = dossier.forme === "ei" || dossier.forme === "eurl" ? dossier.forme : undefined;
    const activiteDossier = dossier.activite !== "cipav" ? dossier.activite : undefined;
    // Un bénéfice sous le minimum du curseur ne se reprend pas : la question reste.
    const beneficeDossier = dossier.benefice >= 5000 ? dossier.benefice : undefined;
    const [forme, setForme] = useState(formeDossier ?? null);
    const [activite, setActivite] = useState(activiteDossier ?? null);
    const [benefice, setBenefice] = useState(beneficeDossier ?? 60000);
    const [capital, setCapital] = useState(1000);
    const [besoin, setBesoin] = useState(dossier.besoin ?? 30000);
    const [enCouple, setEnCouple] = useState(dossier.enCouple ?? false);
    const [enfants, setEnfants] = useState(dossier.enfants ?? 0);
    const [autresRevenus, setAutresRevenus] = useState(dossier.autresRevenus ?? 0);

    const { etape, communs, rang, aller, retour, recommencer, reduit } = useParcours(
        "forme",
        () => {
            setForme(null);
            setActivite(null);
            setBenefice(60000);
            setCapital(1000);
            setBesoin(30000);
            setEnCouple(false);
            setEnfants(0);
            setAutresRevenus(0);
        },
        PHASES,
        reprendre(
            dossier.forme === "micro"
                ? [{ etape: "forme", connu: true }, { etape: "micro" }]
                : formeDossier !== undefined && dossier.activite === "cipav"
                  ? [
                        { etape: "forme", connu: true },
                        { etape: "activite", connu: true },
                        { etape: "cipav" },
                    ]
                  : [
                        { etape: "forme", connu: formeDossier !== undefined },
                        { etape: "activite", connu: activiteDossier !== undefined },
                        { etape: "benefice", connu: beneficeDossier !== undefined },
                        // Le capital social ne se déduit pas du dossier : le parcours s'y arrête.
                        ...(formeDossier === "eurl" ? [{ etape: "capital", connu: false }] : []),
                        { etape: "besoin", connu: dossier.besoin !== undefined },
                        { etape: "foyer", connu: dossier.enCouple !== undefined && dossier.enfants !== undefined },
                        { etape: "autresRevenus", connu: dossier.autresRevenus !== undefined },
                        { etape: "resultat" },
                    ],
        ),
    );

    useSignalerDossier({ forme, activite, besoin, enCouple, enfants, autresRevenus }, etape === "resultat");

    const entree = useMemo(
        () => ({
            forme,
            benefice,
            capital,
            besoin: Math.min(besoin, benefice),
            activite: activite === "artisan" ? "services" : activite || "bnc",
            artisan: activite === "artisan",
            foyer: { enCouple, enfants, autresRevenus },
        }),
        [forme, benefice, capital, besoin, activite, enCouple, enfants, autresRevenus],
    );
    const calcul = useMemo(() => (etape === "resultat" ? comparerOptionIs(entree) : null), [etape, entree]);
    // Chaque montant du résultat dit d'où il vient : ses étapes et ses sources.
    const ex = useMemo(() => (calcul ? expliquerOptionIs(entree, calcul, lang) : null), [calcul, entree, lang]);
    const { ouverte, basculer } = useExplications();

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };
    const sommes = (v) => euros(Math.round(v), lang);
    const lienOutil = (fr, en, libelle, type = "primary") => (
        <Link href={{ pathname: "/tools/[slug]", params: { slug: lang === "fr" ? fr : en } }}>
            <Button type={type} className="press-safe w-full">
                {libelle} →
            </Button>
        </Link>
    );

    if (etape === "forme") {
        return (
            <Question
                key="forme"
                {...communsEcran}
                phase={PHASES.forme}
                rang={rang}
                titre={t.forme.titre}
                aide={t.forme.aide}
                choix={t.forme.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        if (c.cle === "micro") {
                            aller("micro");
                            return;
                        }
                        setForme(c.cle);
                        aller("activite");
                    },
                }))}
            />
        );
    }

    if (etape === "micro" || etape === "cipav") {
        const texte = t[etape];
        return (
            <Ecran key={etape} {...communsEcran} phase={PHASES[etape]} rang={rang} titre={texte.titre} aide={texte.aide}>
                <Liste items={texte.points} />
                {etape === "micro" && (
                    <div className="mt-5">
                        {lienOutil(
                            "quand-quitter-la-micro-entreprise",
                            "when-to-leave-the-micro-entreprise",
                            t.micro.lien,
                        )}
                    </div>
                )}
                <Recommencer onClick={recommencer} libelle={r.recommencer} />
            </Ecran>
        );
    }

    if (etape === "activite") {
        return (
            <Question
                key="activite"
                {...communsEcran}
                phase={PHASES.activite}
                rang={rang}
                titre={t.activite.titre}
                aide={t.activite.aide}
                choix={t.activite.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        if (c.cle === "cipav") {
                            aller("cipav");
                            return;
                        }
                        setActivite(c.cle);
                        aller("benefice");
                    },
                }))}
            />
        );
    }

    if (etape === "benefice") {
        return (
            <Ecran key="benefice" {...communsEcran} phase={PHASES.benefice} rang={rang} titre={t.benefice.titre} aide={t.benefice.aide}>
                <Curseur
                    libelle={t.benefice.libelle}
                    valeur={benefice}
                    poser={setBenefice}
                    minimum={5000}
                    maximum={300000}
                    saisie
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller(forme === "eurl" ? "capital" : "besoin")}>{t.benefice.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "capital") {
        return (
            <Ecran
                key="capital"
                {...communsEcran}
                phase={PHASES.capital}
                rang={rang}
                titre={t.capital.titre}
                aide={remplir(t.capital.aide, { seuil: `${SEUIL_DIVIDENDES_EURL * 100} %` })}
            >
                <Curseur
                    libelle={t.capital.libelle}
                    valeur={capital}
                    poser={setCapital}
                    minimum={0}
                    maximum={100000}
                    saisie
                    pas={500}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("besoin")}>{t.capital.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "besoin") {
        return (
            <Ecran key="besoin" {...communsEcran} phase={PHASES.besoin} rang={rang} titre={t.besoin.titre} aide={t.besoin.aide}>
                <Curseur
                    libelle={t.besoin.libelle}
                    valeur={Math.min(besoin, benefice)}
                    poser={setBesoin}
                    minimum={0}
                    maximum={benefice}
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("foyer")}>{t.besoin.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "foyer") {
        return (
            <Ecran key="foyer" {...communsEcran} phase={PHASES.foyer} rang={rang} titre={t.foyer.titre} aide={t.foyer.aide}>
                <div className="space-y-5">
                    <div>
                        <div className="text-sm text-gray-600 dark:text-lightBlue mb-2">{t.foyer.situation}</div>
                        <Segments
                            options={[
                                { cle: "seul", libelle: t.foyer.seul },
                                { cle: "couple", libelle: t.foyer.couple },
                            ]}
                            valeur={enCouple ? "couple" : "seul"}
                            poser={(v) => setEnCouple(v === "couple")}
                        />
                    </div>
                    <Curseur
                        libelle={t.foyer.enfants}
                        valeur={enfants}
                        poser={setEnfants}
                        minimum={0}
                        maximum={6}
                        pas={1}
                        formater={(v) => String(v)}
                    />
                </div>
                <Avancer onClick={() => aller("autresRevenus")}>{t.foyer.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "autresRevenus") {
        return (
            <Ecran
                key="autresRevenus"
                {...communsEcran}
                phase={PHASES.autresRevenus}
                rang={rang}
                titre={t.autresRevenus.titre}
                aide={t.autresRevenus.aide}
            >
                <Curseur
                    libelle={t.autresRevenus.libelle}
                    aide={autresRevenus === 0 ? t.autresRevenus.aucun : undefined}
                    valeur={autresRevenus}
                    poser={setAutresRevenus}
                    minimum={0}
                    maximum={120000}
                    saisie
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("resultat")}>{t.autresRevenus.suivant}</Avancer>
            </Ecran>
        );
    }

    const { ir, is, gain, gagnant, garde } = calcul;
    const besoinRetenu = Math.min(besoin, benefice);
    const seuil = forme === "ei" ? r.garde.seuilEi : sommes(capital * SEUIL_DIVIDENDES_EURL);

    return (
        <Resultat onRetour={retour} retour={t.retour} key="resultat" titre={r.titre} phases={t.phases} {...communs}>
            <div className="mt-2 font-bold text-2xl leading-tight dark:text-white">
                {remplir(r.toutTitre[gagnant || "egal"], { gain: sommes(Math.abs(gain)) })}
            </div>
            <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                {phraseAvecMontants(r.toutDetail, {
                    ir: (
                        <MontantExplicable explication={ex.ir} ouvert={ouverte === "ir"} onBasculer={() => basculer("ir")}>
                            {sommes(ir.net)}
                        </MontantExplicable>
                    ),
                    is: (
                        <MontantExplicable explication={ex.is} ouvert={ouverte === "is"} onBasculer={() => basculer("is")}>
                            {sommes(is.meilleur.net)}
                        </MontantExplicable>
                    ),
                })}
            </p>
            {ouverte === "ir" && <PanneauExplication explication={ex.ir} lang={lang} />}
            {ouverte === "is" && <PanneauExplication explication={ex.is} lang={lang} />}
            <IndiceExplication lang={lang} />
            {/* L'explication ne vaut que si l'IS gagne en se versant tout en
                rémunération : avec des dividendes, l'écart vient d'ailleurs. */}
            {gagnant === "is" && is.meilleur.part > 0.995 && (
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{r.pourquoi}</p>
            )}

            {/* LE VRAI USAGE DE L'IS : GARDER. L'encadré chiffre ce que
                l'entreprise conserve quand on ne prend que son besoin, et dit
                aussitôt ce que cet argent coûtera en sortant. */}
            {besoinRetenu > 0 && garde && (
                <div className="mt-6 rounded-xl border border-primary/30 bg-primary/[0.06] px-4 py-4">
                    <div className="flex items-center gap-2 font-semibold dark:text-white">
                        <SolarIcon name="safe-square-linear" className="w-5 h-5 text-primary" />
                        {remplir(r.garde.titre, { besoin: sommes(besoinRetenu) })}
                    </div>
                    {garde.atteignable ? (
                        <>
                            <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                                {remplir(garde.irSuffit ? r.garde.texte : r.garde.texteCourt, {
                                    epargne: sommes(garde.epargneIr),
                                    reserve: sommes(garde.reserve),
                                    impot: sommes(garde.impotSocietes),
                                    ir: sommes(ir.net),
                                    besoin: sommes(besoinRetenu),
                                })}
                            </p>
                            <div className="mt-3 grid grid-cols-2 gap-3 text-center">
                                <div className="rounded-lg bg-white/70 dark:bg-white/[0.04] px-3 py-2">
                                    <div className="text-[11px] text-gray-500 dark:text-lightBlue/70">{r.garde.carteIr}</div>
                                    <div className="font-bold tabular-nums dark:text-white">
                                        <MontantExplicable explication={ex.epargne} ouvert={ouverte === "epargne"} onBasculer={() => basculer("epargne")}>
                                            <Montant valeur={Math.round(garde.epargneIr)} lang={lang} reduit={reduit} />
                                        </MontantExplicable>
                                    </div>
                                </div>
                                <div className="rounded-lg bg-white/70 dark:bg-white/[0.04] px-3 py-2">
                                    <div className="text-[11px] text-gray-500 dark:text-lightBlue/70">{r.garde.carteIs}</div>
                                    <div className="font-bold tabular-nums dark:text-white">
                                        <MontantExplicable explication={ex.reserve} ouvert={ouverte === "reserve"} onBasculer={() => basculer("reserve")}>
                                            <Montant valeur={Math.round(garde.reserve)} lang={lang} reduit={reduit} />
                                        </MontantExplicable>
                                    </div>
                                </div>
                            </div>
                            {ouverte === "epargne" && <PanneauExplication explication={ex.epargne} lang={lang} />}
                            {ouverte === "reserve" && <PanneauExplication explication={ex.reserve} lang={lang} />}
                            <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                                {remplir(r.garde.piege, { seuil })}
                            </p>
                        </>
                    ) : (
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{r.garde.impossible}</p>
                    )}
                </div>
            )}

            <div className="mt-6">
                <div className="font-semibold dark:text-white">{r.regles.titre}</div>
                <Liste
                    items={[
                        r.regles.items.delai,
                        r.regles.items.retour,
                        ...(forme === "ei" ? [r.regles.items.ei] : []),
                    ]}
                />
            </div>

            <div className="mt-6">
                {lienOutil("remuneration-ou-dividendes", "salary-or-dividends", r.lienPartage, "secondary")}
            </div>

            <div className="mt-6">
                <div className="font-semibold dark:text-white">{r.hypotheses.titre}</div>
                <Liste items={r.hypotheses.items} />
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-6 mb-0 leading-relaxed">{r.avertissement}</p>

            <Recommencer onClick={recommencer} libelle={r.recommencer} />
        </Resultat>
    );
}
